import React, { useMemo } from 'react';
import { getSmoothStepPath, useStore } from '@xyflow/react';

/**
 * SmartEdge - A custom edge component that routes around nodes
 * while respecting handle positions (side handles exit horizontally,
 * top/bottom handles exit vertically).
 */

// Padding around nodes for edge routing
const NODE_PADDING = 25;

/**
 * Check if a point is inside a rectangle (node bounds)
 */
const isPointInRect = (x, y, rect) => {
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
};

/**
 * Check if a line segment intersects with a rectangle
 */
const lineIntersectsRect = (x1, y1, x2, y2, rect) => {
    if (isPointInRect(x1, y1, rect) || isPointInRect(x2, y2, rect)) {
        return true;
    }

    const edges = [
        { x1: rect.left, y1: rect.top, x2: rect.right, y2: rect.top },
        { x1: rect.right, y1: rect.top, x2: rect.right, y2: rect.bottom },
        { x1: rect.left, y1: rect.bottom, x2: rect.right, y2: rect.bottom },
        { x1: rect.left, y1: rect.top, x2: rect.left, y2: rect.bottom }
    ];

    for (const edge of edges) {
        if (lineSegmentsIntersect(x1, y1, x2, y2, edge.x1, edge.y1, edge.x2, edge.y2)) {
            return true;
        }
    }

    return false;
};

/**
 * Check if two line segments intersect
 */
const lineSegmentsIntersect = (x1, y1, x2, y2, x3, y3, x4, y4) => {
    const denom = ((y4 - y3) * (x2 - x1)) - ((x4 - x3) * (y2 - y1));
    if (denom === 0) return false;

    const ua = (((x4 - x3) * (y1 - y3)) - ((y4 - y3) * (x1 - x3))) / denom;
    const ub = (((x2 - x1) * (y1 - y3)) - ((y2 - y1) * (x1 - x3))) / denom;

    return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
};

/**
 * Get bounding box for a node with padding
 */
const getNodeBounds = (node, padding = NODE_PADDING) => {
    const width = node.measured?.width || node.width || 200;
    const height = node.measured?.height || node.height || 80;

    return {
        left: node.position.x - padding,
        top: node.position.y - padding,
        right: node.position.x + width + padding,
        bottom: node.position.y + height + padding,
        centerX: node.position.x + width / 2,
        centerY: node.position.y + height / 2
    };
};

/**
 * Check if any segment of a path intersects with blocking nodes
 */
const pathIntersectsNodes = (points, blockingNodes) => {
    for (let i = 0; i < points.length - 1; i++) {
        const p1 = points[i];
        const p2 = points[i + 1];

        for (const { bounds } of blockingNodes) {
            if (lineIntersectsRect(p1.x, p1.y, p2.x, p2.y, bounds)) {
                return true;
            }
        }
    }
    return false;
};

/**
 * Find nodes that could block the edge path
 */
const findBlockingNodes = (sourceX, sourceY, targetX, targetY, nodes, sourceId, targetId) => {
    const blockingNodes = [];

    // Calculate the bounding box of the path area
    const pathBounds = {
        left: Math.min(sourceX, targetX) - NODE_PADDING,
        right: Math.max(sourceX, targetX) + NODE_PADDING,
        top: Math.min(sourceY, targetY) - NODE_PADDING,
        bottom: Math.max(sourceY, targetY) + NODE_PADDING
    };

    for (const node of nodes) {
        if (node.id === sourceId || node.id === targetId) continue;

        const bounds = getNodeBounds(node, NODE_PADDING);

        // Check if node is in the general path area
        if (bounds.right >= pathBounds.left && bounds.left <= pathBounds.right &&
            bounds.bottom >= pathBounds.top && bounds.top <= pathBounds.bottom) {
            blockingNodes.push({ node, bounds });
        }
    }

    return blockingNodes;
};

/**
 * Generate waypoints that route around blocking nodes
 * Respects handle positions for proper edge direction
 */
const calculateSmartPath = (
    sourceX, sourceY, targetX, targetY,
    sourcePosition, targetPosition,
    blockingNodes
) => {
    // Minimum offset for edges to clear handles
    const HANDLE_OFFSET = 40;

    // Determine if this is primarily horizontal or vertical flow
    const isHorizontalFlow = sourcePosition === 'right' || sourcePosition === 'left' ||
                            targetPosition === 'right' || targetPosition === 'left';

    // For side handles, we need to go out horizontally first
    const sourceIsRight = sourcePosition === 'right';
    const sourceIsLeft = sourcePosition === 'left';
    const targetIsLeft = targetPosition === 'left';
    const targetIsRight = targetPosition === 'right';
    const targetIsTop = targetPosition === 'top';
    const targetIsBottom = targetPosition === 'bottom';

    // If no blocking nodes, create a simple stepped path
    if (blockingNodes.length === 0) {
        // For right-to-top connections (retargeting/bridge node style)
        if (sourceIsRight && targetIsTop) {
            // Determine the horizontal routing point
            // If target is to the right, go right then turn
            // If target is to the left, we need to go right first, then loop back
            const targetIsRightOfSource = targetX > sourceX;

            if (targetIsRightOfSource) {
                // Target is to the right - simple right-then-down path
                const midX = Math.max(sourceX + HANDLE_OFFSET, targetX);
                return [
                    { x: sourceX, y: sourceY },
                    { x: midX, y: sourceY },
                    { x: midX, y: targetY - HANDLE_OFFSET },
                    { x: targetX, y: targetY - HANDLE_OFFSET },
                    { x: targetX, y: targetY }
                ];
            } else {
                // Target is to the left - go right, up/down, then left to target
                const midX = sourceX + HANDLE_OFFSET;
                const routeY = Math.min(sourceY, targetY) - HANDLE_OFFSET;
                return [
                    { x: sourceX, y: sourceY },
                    { x: midX, y: sourceY },
                    { x: midX, y: routeY },
                    { x: targetX, y: routeY },
                    { x: targetX, y: targetY }
                ];
            }
        }

        // For right-to-left connections (horizontal flow between nodes)
        if (sourceIsRight && targetIsLeft) {
            const midX = (sourceX + targetX) / 2;
            return [
                { x: sourceX, y: sourceY },
                { x: midX, y: sourceY },
                { x: midX, y: targetY },
                { x: targetX, y: targetY }
            ];
        }

        // For bottom-to-top connections (standard hierarchy)
        // Use a consistent horizontal routing level above the target
        // so multiple inputs to the same node align horizontally
        if (sourcePosition === 'bottom' && targetIsTop) {
            const routeY = targetY - HANDLE_OFFSET;
            return [
                { x: sourceX, y: sourceY },
                { x: sourceX, y: routeY },
                { x: targetX, y: routeY },
                { x: targetX, y: targetY }
            ];
        }

        // Default case
        return null; // Use default smoothstep
    }

    // With blocking nodes, we need to route around them
    const points = [];
    points.push({ x: sourceX, y: sourceY });

    // Calculate the combined bounding box of all blocking nodes
    let minLeft = Infinity, maxRight = -Infinity;
    let minTop = Infinity, maxBottom = -Infinity;

    for (const { bounds } of blockingNodes) {
        minLeft = Math.min(minLeft, bounds.left);
        maxRight = Math.max(maxRight, bounds.right);
        minTop = Math.min(minTop, bounds.top);
        maxBottom = Math.max(maxBottom, bounds.bottom);
    }

    // For side-handle connections (retargeting), try to route around
    if (sourceIsRight) {
        // Exit horizontally to the right
        const exitX = Math.max(sourceX + HANDLE_OFFSET, maxRight + NODE_PADDING);
        points.push({ x: exitX, y: sourceY });

        // Route vertically
        if (targetIsTop) {
            // Go to the target's X position, above blocking nodes
            const routeY = Math.min(minTop - NODE_PADDING, targetY - HANDLE_OFFSET);
            points.push({ x: exitX, y: routeY });
            points.push({ x: targetX, y: routeY });
        } else {
            // Direct horizontal then vertical
            points.push({ x: exitX, y: targetY });
            points.push({ x: targetX, y: targetY });
        }
    } else if (sourcePosition === 'bottom') {
        // Use consistent horizontal routing level above target for alignment
        const routeY = targetY - HANDLE_OFFSET;

        // Check if we need to route around nodes
        const directPath = [
            { x: sourceX, y: sourceY },
            { x: sourceX, y: routeY },
            { x: targetX, y: routeY },
            { x: targetX, y: targetY }
        ];

        if (pathIntersectsNodes(directPath, blockingNodes)) {
            // Route around - go to the side with more clearance
            const goLeft = sourceX > (minLeft + maxRight) / 2;
            const routeX = goLeft ? minLeft - NODE_PADDING : maxRight + NODE_PADDING;

            points.push({ x: sourceX, y: sourceY + HANDLE_OFFSET });
            points.push({ x: routeX, y: sourceY + HANDLE_OFFSET });
            points.push({ x: routeX, y: routeY });
            points.push({ x: targetX, y: routeY });
        } else {
            // No collision - use the consistent horizontal level
            points.push({ x: sourceX, y: routeY });
            points.push({ x: targetX, y: routeY });
        }
    } else {
        // Default: try to find a clear path
        const midY = (sourceY + targetY) / 2;

        // Check if horizontal path at midY is blocked
        const horizontalBlocked = blockingNodes.some(({ bounds }) =>
            midY >= bounds.top && midY <= bounds.bottom
        );

        if (horizontalBlocked) {
            // Route above or below the blocking area
            const goAbove = sourceY < (minTop + maxBottom) / 2;
            const routeY = goAbove ? minTop - NODE_PADDING : maxBottom + NODE_PADDING;

            points.push({ x: sourceX, y: routeY });
            points.push({ x: targetX, y: routeY });
        } else {
            points.push({ x: sourceX, y: midY });
            points.push({ x: targetX, y: midY });
        }
    }

    points.push({ x: targetX, y: targetY });

    // Remove duplicate consecutive points
    const cleanedPoints = [points[0]];
    for (let i = 1; i < points.length; i++) {
        const prev = cleanedPoints[cleanedPoints.length - 1];
        if (Math.abs(points[i].x - prev.x) > 1 || Math.abs(points[i].y - prev.y) > 1) {
            cleanedPoints.push(points[i]);
        }
    }

    return cleanedPoints;
};

/**
 * Generate SVG path from waypoints with rounded corners
 */
const generatePath = (points, borderRadius = 12) => {
    if (!points || points.length < 2) return '';

    if (points.length === 2) {
        return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
    }

    let path = `M ${points[0].x},${points[0].y}`;

    for (let i = 1; i < points.length - 1; i++) {
        const prev = points[i - 1];
        const curr = points[i];
        const next = points[i + 1];

        // Calculate direction vectors
        const d1x = curr.x - prev.x;
        const d1y = curr.y - prev.y;
        const d2x = next.x - curr.x;
        const d2y = next.y - curr.y;

        const len1 = Math.hypot(d1x, d1y);
        const len2 = Math.hypot(d2x, d2y);

        if (len1 === 0 || len2 === 0) {
            path += ` L ${curr.x},${curr.y}`;
            continue;
        }

        // Calculate the corner radius (limited by segment length)
        const radius = Math.min(borderRadius, len1 / 2, len2 / 2);

        // Points before and after the corner
        const beforeX = curr.x - (d1x / len1) * radius;
        const beforeY = curr.y - (d1y / len1) * radius;
        const afterX = curr.x + (d2x / len2) * radius;
        const afterY = curr.y + (d2y / len2) * radius;

        path += ` L ${beforeX},${beforeY}`;
        path += ` Q ${curr.x},${curr.y} ${afterX},${afterY}`;
    }

    // Final segment
    const lastPoint = points[points.length - 1];
    path += ` L ${lastPoint.x},${lastPoint.y}`;

    return path;
};

/**
 * SmartEdge component
 */
const SmartEdge = ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    markerEnd,
    source,
    target,
    animated = false,
    selected = false
}) => {
    const nodes = useStore((state) => state.nodes);

    const { path, labelX, labelY } = useMemo(() => {
        // Find nodes that could block the path
        const blockingNodes = findBlockingNodes(
            sourceX, sourceY, targetX, targetY,
            nodes, source, target
        );

        // Calculate smart path respecting handle positions
        const waypoints = calculateSmartPath(
            sourceX, sourceY, targetX, targetY,
            sourcePosition, targetPosition,
            blockingNodes
        );

        // If we have custom waypoints, use them
        if (waypoints && waypoints.length > 2) {
            // Check if this path actually collides with anything
            if (!pathIntersectsNodes(waypoints, blockingNodes)) {
                const customPath = generatePath(waypoints, 12);

                // Calculate label position at the middle of the path
                const midIdx = Math.floor(waypoints.length / 2);
                const labelPos = waypoints[midIdx];

                return {
                    path: customPath,
                    labelX: labelPos.x,
                    labelY: labelPos.y
                };
            }
        }

        // Fallback to smoothstep if no blocking nodes or custom path failed
        const [edgePath, labelX, labelY] = getSmoothStepPath({
            sourceX,
            sourceY,
            targetX,
            targetY,
            sourcePosition,
            targetPosition,
            borderRadius: 12
        });

        return { path: edgePath, labelX, labelY };
    }, [sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, nodes, source, target]);

    const strokeColor = selected ? 'var(--color-primary)' : (style.stroke || 'var(--color-border)');

    return (
        <g className={`react-flow__edge ${animated ? 'animated' : ''}`}>
            <path
                id={id}
                className="react-flow__edge-path"
                d={path}
                style={{
                    ...style,
                    stroke: strokeColor,
                    strokeWidth: style.strokeWidth || 2,
                    fill: 'none'
                }}
                markerEnd={markerEnd}
            />
            {/* Wider invisible path for easier selection */}
            <path
                d={path}
                style={{
                    stroke: 'transparent',
                    strokeWidth: 20,
                    fill: 'none',
                    cursor: 'pointer'
                }}
            />
            {/* Animated dashed overlay for retargeting edges */}
            {animated && (
                <path
                    d={path}
                    style={{
                        stroke: strokeColor,
                        strokeWidth: style.strokeWidth || 2,
                        fill: 'none',
                        strokeDasharray: '5 5',
                        animation: 'dashdraw 0.5s linear infinite'
                    }}
                />
            )}
        </g>
    );
};

export default SmartEdge;
